import React from "react";

export type SelectOption = { value: string; label: string };

type MinimalFormik =
  | {
      values: Record<string, any>;
      errors?: Record<string, any>;
      touched?: Record<string, any>;
      handleChange?: (e: any) => void;
      handleBlur?: (e: any) => void;
      setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => any;
    }
  | undefined;

type BaseFieldProps = {
  name: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  labelClassName?: string;
  inputClassName?: string;
 // ✅ NEW
  hideZero?: boolean;
  // EITHER give formik...
  formik?: MinimalFormik;

  // ...OR a controlled value/onChange
  value?: any;
  onChange?: (value: any) => void;
  onBlur?: () => void;
};

/* ===========================================================
   UIInput (NO hooks, safe for table cells + warehouse pages)
   =========================================================== */
// export const UIInput: React.FC<
//   BaseFieldProps & React.InputHTMLAttributes<HTMLInputElement>
// > = React.memo(
//   ({
//     name,
//     label,
//     placeholder,
//     required,
//     disabled,
//     className,
//     labelClassName,
//     inputClassName,
//     formik,
//     value,
//     onChange,
//     onBlur,
//     ...rest
//   }) => {
//     const fv = formik?.values?.[name];
//     const v = value !== undefined ? value : fv ?? "";
//     const err = formik?.errors?.[name];
//     const touched = formik?.touched?.[name];

//     const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
//       if (formik?.handleChange) {
//         formik.handleChange(e);
//       } else if (formik?.setFieldValue) {
//         formik.setFieldValue(name, e.target.value);
//       } else if (onChange) {
//         onChange(e.target.value);
//       }
//     };

//     const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
//       if (formik?.handleBlur) {
//         formik.handleBlur(e);
//       } else if (onBlur) {
//         onBlur();
//       }
//     };

//     return (
//       <div className={className}>
//         {label !== undefined && (
//           <label className={`form-label ${labelClassName || ""}`}>
//             {label} {required ? <span className="text-danger">*</span> : null}
//           </label>
//         )}
//         <input
//           name={name}
//           value={v}
//           placeholder={placeholder}
//           disabled={disabled}
//           onChange={handleChange}
//           onBlur={handleBlur}
//           className={`form-control ${inputClassName || ""} ${
//             touched && err ? "is-invalid" : ""
//           }`}
//           {...rest}
//         />
//         {touched && err ? (
//           <div className="invalid-feedback" style={{ display: "block" }}>
//             {String(err)}
//           </div>
//         ) : null}
//       </div>
//     );
//   }
// );
export const UIInput: React.FC<
  BaseFieldProps & React.InputHTMLAttributes<HTMLInputElement>
> = React.memo(
  ({
    name,
    label,
    placeholder,
    required,
    disabled,
    className,
    labelClassName,
    inputClassName,
    formik,
    value,
    onChange,
    onBlur,
    hideZero,
    ...rest
  }) => {
    const fv = formik?.values?.[name];
    const rawValue = value !== undefined ? value : fv ?? "";

    // ✅ if hideZero and value is 0, show ""
    const v =
      hideZero && (rawValue === 0 || rawValue === "0") ? "" : rawValue;

    const err = formik?.errors?.[name];
    const touched = formik?.touched?.[name];

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputType = (rest as any)?.type;

      // ✅ if numeric + hideZero: store 0 when empty, else store number
      let next: any = e.target.value;

      if (hideZero && inputType === "number") {
        next = next === "" ? 0 : Number(next);
        if (!Number.isFinite(next)) next = 0;
      }

      if (formik?.handleChange) {
        // Formik handleChange expects the event (keep normal behavior)
        formik.handleChange(e);
        // but if we want numeric coercion reliably, prefer setFieldValue below
        if (hideZero && inputType === "number" && formik?.setFieldValue) {
          formik.setFieldValue(name, next);
        }
      } else if (formik?.setFieldValue) {
        formik.setFieldValue(name, next);
      } else if (onChange) {
        onChange(next);
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (formik?.handleBlur) formik.handleBlur(e);
      else if (onBlur) onBlur();
    };

    return (
      <div className={className}>
        {label !== undefined && (
          <label className={`form-label ${labelClassName || ""}`}>
            {label} {required ? <span className="text-danger">*</span> : null}
          </label>
        )}

        <input
          name={name}
          value={v}
          placeholder={placeholder}
          disabled={disabled}
          onChange={handleChange}
          onBlur={handleBlur}
          className={`form-control ${inputClassName || ""} ${
            touched && err ? "is-invalid" : ""
          }`}
          {...rest}
        />

        {touched && err ? (
          <div className="invalid-feedback" style={{ display: "block" }}>
            {String(err)}
          </div>
        ) : null}
      </div>
    );
  }
);

/* ===========================================================
   UIReactSelect (basic <select>, NO hooks, Formik-friendly)
   =========================================================== */
export const UIReactSelect: React.FC<
  BaseFieldProps & { options: SelectOption[] }
> = React.memo(
  ({
    name,
    label,
    placeholder,
    required,
    disabled,
    className,
    labelClassName,
    inputClassName,
    formik,
    value,
    onChange,
    onBlur,
    options,
  }) => {
    const fv = formik?.values?.[name];
    const v = value !== undefined ? value : fv ?? "";

    const err = formik?.errors?.[name];
    const touched = formik?.touched?.[name];

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const next = e.target.value;
      if (formik?.handleChange) {
        formik.handleChange(e);
      } else if (formik?.setFieldValue) {
        formik.setFieldValue(name, next);
      } else if (onChange) {
        onChange(next);
      }
    };

    const handleBlur = () => {
      if (formik?.handleBlur) {
        // fabricate a blur event signature that Formik accepts
        formik.handleBlur({ target: { name } } as any);
      } else if (onBlur) {
        onBlur();
      }
    };

    return (
      <div className={className}>
        {label !== undefined && (
          <label className={`form-label ${labelClassName || ""}`}>
            {label} {required ? <span className="text-danger">*</span> : null}
          </label>
        )}
        <select
          name={name}
          value={v}
          disabled={disabled}
          onChange={handleChange}
          onBlur={handleBlur}
          className={`form-select ${inputClassName || ""} ${
            touched && err ? "is-invalid" : ""
          }`}
        >
          {placeholder !== undefined ? (
            <option value="">{placeholder}</option>
          ) : null}
          {options.map((o) => (
            <option key={String(o.value)} value={String(o.value)}>
              {o.label}
            </option>
          ))}
        </select>
        {touched && err ? (
          <div className="invalid-feedback" style={{ display: "block" }}>
            {String(err)}
          </div>
        ) : null}
      </div>
    );
  }
);
